// src/pages/Arbitrage/QuizzVideoPage.jsx (adapter le chemin si besoin)
import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import Button from "../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import { getQuizzVideos } from "../../services/quizzvideoServices";
import QuizzVideoCard from "./QuizzVideoCard";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import PageHeader from "../../components/Layout/PageHeader";
import Select from "react-select";

const QuizzVideoPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [quizzVideos, setQuizzVideos] = useState([]);
  const [displayedQuizz, setDisplayedQuizz] = useState([]);
  const [family, setFamily] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuizzVideos = async () => {
      try {
        setLoading(true);
        const data = await getQuizzVideos();
        const list = Array.isArray(data) ? data : [];
        setQuizzVideos(list);
        // par défaut : les 12 premiers
        setDisplayedQuizz(list.slice(0, 12));
      } catch (error) {
        console.error("Erreur lors du chargement des quizz vidéo :", error);
        toast.error("Erreur lors du chargement des quizz vidéo.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzVideos();
  }, []);

  // Options de familles pour le select
  const familyOptions = useMemo(() => {
    const families = Array.from(
      new Set(quizzVideos.map((qv) => qv.famille_quizz).filter(Boolean))
    ).sort((a, b) =>
      String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );

    return [
      { value: "", label: "Toutes les familles" },
      ...families.map((fam) => ({
        value: fam,
        label: fam,
      })),
    ];
  }, [quizzVideos]);

  const selectedFamilyOption =
    familyOptions.find((o) => o.value === family) || familyOptions[0];

  const handleFamilyChange = (selectedOption) => {
    const familyValue = selectedOption ? selectedOption.value : "";
    setFamily(familyValue);

    if (!familyValue) {
      // reset : 12 premiers
      setDisplayedQuizz(quizzVideos.slice(0, 12));
      return;
    }

    const filtered = quizzVideos.filter(
      (qv) => qv.famille_quizz === familyValue
    );
    setDisplayedQuizz(filtered);
  };

  const getRandomQuizz = () => {
    if (quizzVideos.length === 0) return;
    const shuffled = [...quizzVideos].sort(() => 0.5 - Math.random());
    setDisplayedQuizz(shuffled.slice(0, 10));
    setFamily(""); // on sort du filtre famille
  };

  const nbDisplayed = displayedQuizz.length;
  const total = quizzVideos.length;

  // Styles react-select (simple, propre)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "white",
      borderColor: state.isFocused ? "#6366f1" : "#cbd5f5",
      minHeight: "40px",
      boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
      "&:hover": {
        borderColor: "#6366f1",
      },
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 40,
      fontSize: "0.85rem",
    }),
  };

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Arbitres & commissaires sportifs"
        title="Quizz vidéo d’arbitrage"
        subtitle="Entraînez-vous sur des situations réelles en vidéo : visionnez l’action, prenez votre décision, puis consultez la correction détaillée."
        align="center"
        size="md"
      />

      <div className="w-full max-w-6xl mx-auto space-y-4">
        {/* Panel filtres / actions */}
        <div className="rounded-2xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/70 dark:border-slate-700/70 shadow-sm px-4 py-4 sm:px-6 flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Select famille */}
            <div className="flex-1 min-w-[240px]">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Filtrer par famille de quizz
              </label>
              <Select
                value={selectedFamilyOption}
                onChange={handleFamilyChange}
                options={familyOptions}
                styles={selectStyles}
                className="w-full"
                classNamePrefix="quizz-family-select"
                isClearable={false}
              />
            </div>

            {/* Bouton aléatoire */}
            <div className="flex items-end justify-start lg:justify-end">
              <Button
                text="10 quizz aléatoires"
                onClick={getRandomQuizz}
                className="!rounded-xl !px-4 !py-2 text-sm"
              />
            </div>
          </div>

          {/* Compteur */}
          <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 flex flex-wrap gap-2 justify-between">
            <span>
              Quizz affichés :{" "}
              <span className="font-semibold text-primary">{nbDisplayed}</span>
              {total > 0 && (
                <>
                  {" "}
                  <span className="opacity-70">/ {total} disponibles</span>
                </>
              )}
            </span>
            {family && (
              <span className="text-[0.7rem] sm:text-xs inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1">
                Famille filtrée :{" "}
                <span className="ml-1 font-semibold">{family}</span>
              </span>
            )}
          </div>
        </div>

        {/* Liste des quizz */}
        <div className="mt-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
              Chargement des quizz vidéo…
            </div>
          ) : nbDisplayed === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">
              Aucun quizz ne correspond à vos critères. Modifiez la famille ou
              lancez une sélection aléatoire.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1280px] mx-auto mb-4 mt-4">
              {displayedQuizz.map((quizz) => (
                <QuizzVideoCard key={quizz.id} quizz={quizz} />
              ))}
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default QuizzVideoPage;
